import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Plus, Trash, PencilSimple, X, FloppyDisk } from "@phosphor-icons/react";

export default function Crews() {
  const [crews, setCrews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null); // crew object or {new:true}

  const load = async () => {
    const [c, e] = await Promise.all([api.get("/crews"), api.get("/employees")]);
    setCrews(c.data); setEmployees(e.data);
    try { const u = await api.get("/users"); setUsers(u.data.filter((x) => x.role === "foreman")); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { name: editing.name, foreman_id: editing.foreman_id || null, member_ids: editing.member_ids || [] };
    if (editing.id) await api.put(`/crews/${editing.id}`, payload);
    else await api.post("/crews", payload);
    setEditing(null); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this crew?")) return;
    await api.delete(`/crews/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="crews-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Crews</h1>
          <p className="text-sm font-medium tracking-wide uppercase text-stone-500">Save & edit who was on each crew</p>
        </div>
        <button data-testid="new-crew-btn" onClick={() => setEditing({ new: true, name: "", member_ids: [] })}
          className="h-14 px-6 bg-[#1F3D34] text-white font-bold uppercase border border-stone-200 shadow-sm shadow-stone-900/5 flex items-center gap-2"
        ><Plus weight="bold" /> New Crew</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {crews.map((c) => (
          <div key={c.id} className="bg-white border border-stone-200 p-5 shadow-sm shadow-stone-900/5" data-testid={`crew-${c.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{c.name}</div>
                <div className="text-xs font-mono uppercase text-stone-500">{c.member_ids.length} members</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(c)} className="w-10 h-10 border border-stone-200 flex items-center justify-center hover:bg-[#D8C7A1]"><PencilSimple weight="bold" /></button>
                <button onClick={() => remove(c.id)} className="w-10 h-10 border border-stone-200 flex items-center justify-center hover:bg-red-200"><Trash weight="bold" /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {c.member_ids.map((mid) => {
                const emp = employees.find((e) => e.id === mid);
                return emp ? (<span key={mid} className="text-xs font-bold uppercase border border-stone-300 px-2 py-1">{emp.name}</span>) : null;
              })}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="crew-editor">
          <div className="bg-white border border-stone-200 shadow-2xl shadow-stone-900/10 w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{editing.id ? "Edit Crew" : "New Crew"}</h3>
              <button onClick={() => setEditing(null)} className="w-10 h-10 border border-stone-200 flex items-center justify-center"><X weight="bold" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-bold uppercase tracking-[0.1em] block mb-1">Crew Name</label>
                <input data-testid="crew-name-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="w-full h-14 px-4 bg-white border border-stone-200 text-lg" />
              </div>
              <div>
                <label className="text-sm font-bold uppercase tracking-[0.1em] block mb-1">Foreman</label>
                <select value={editing.foreman_id || ""} onChange={(e) => setEditing({ ...editing, foreman_id: e.target.value })}
                  className="w-full h-14 px-4 bg-white border border-stone-200 text-lg">
                  <option value="">— Unassigned —</option>
                  {users.map((u) => (<option key={u.id} value={u.id}>{u.name} ({u.email})</option>))}
                </select>
              </div>
              <div>
                <label className="text-sm font-bold uppercase tracking-[0.1em] block mb-1">Members</label>
                <div className="border border-stone-200 max-h-72 overflow-y-auto">
                  {employees.map((e) => {
                    const checked = (editing.member_ids || []).includes(e.id);
                    return (
                      <label key={e.id} className="flex items-center gap-3 p-3 border-b border-stone-200 cursor-pointer hover:bg-stone-50">
                        <input data-testid={`crew-member-${e.id}`} type="checkbox" checked={checked} onChange={() => {
                          const next = checked ? editing.member_ids.filter((x) => x !== e.id) : [...(editing.member_ids || []), e.id];
                          setEditing({ ...editing, member_ids: next });
                        }} className="w-5 h-5" />
                        <span className="font-bold">{e.name}</span>
                        <span className="font-mono text-xs ml-auto">{e.employee_code}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              <button data-testid="save-crew-btn" onClick={save}
                className="w-full h-14 bg-[#1F3D34] text-white font-bold uppercase border border-stone-200 shadow-sm shadow-stone-900/5 flex items-center justify-center gap-2"
              ><FloppyDisk weight="bold" /> Save Crew</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
